import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, HTMLAttributes, RefAttributes } from 'react';

import { Icon } from '../icon';
import { cropText } from '../lib/crop-text';
import { Typography } from '../typography';

import styles from './dropdown.scss';
import { Option } from './types';

type SelectOptionsProps = {
  value: string | number;
  options: Option[];
};

const SelectOptions: React.FC<SelectOptionsProps> = ({ options }) => {
  return (
    <>
      {options.map(({ value: optionValue, name, disabled: isDisabled = false }) => (
        <option key={`${optionValue}-${name}`} disabled={isDisabled} value={optionValue}>
          {name}
        </option>
      ))}
    </>
  );
};

type DropdownProps = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    label?: string;
    value: string | number;
    options: Option[];
    error?: boolean;
    disabled?: boolean;
    isRequired?: boolean;
    onChange: (value: string | number) => void;
    onClick?: () => React.MouseEventHandler;
    onBlur?: () => React.FocusEventHandler;
    onFocus?: () => React.FocusEventHandler;
    dataTestId?: string;
  };

const MAX_VIEW_VALUE_WITH_LABEL = 10;

const COMPONENT_ID = 'modulor-dropdown';

export const Dropdown: ForwardRefExoticComponent<DropdownProps> = forwardRef<
  HTMLDivElement,
  DropdownProps
>(
  (
    {
      label = '',
      value = '',
      options = [],
      error = false,
      disabled = false,
      isRequired = false,
      onChange,
      onClick,
      onBlur,
      onFocus,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    if (!Array.isArray(options) || options.length === 0) {
      return null;
    }

    const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
      onChange && onChange(e.target.value);
    };

    const handleClick = () => {
      onClick && onClick();
    };

    const findOptionName = (selectedValue) => {
      const foundName =
        options.find(({ value: optionValue }) => String(optionValue) === String(selectedValue))
          ?.name || '';

      return cropText(foundName, MAX_VIEW_VALUE_WITH_LABEL);
    };

    const hasLabel = label.length > 0;

    return (
      <div
        className={classNames(className, styles.container)}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        ref={ref}
        {...rest}>
        <div
          className={classNames(styles['dropdown-wrapper'], {
            [styles['with-overlay']]: onClick,
            [styles.disabled]: disabled,
          })}
          onClick={handleClick}>
          <select
            data-testid={`${dataTestId}-select`}
            className={classNames(styles.dropdown, {
              [styles.placeholder]: !value,
              [styles.error]: error,
              [styles.disabled]: disabled,
              [styles['with-label']]: hasLabel,
            })}
            value={value}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={handleChange}>
            <SelectOptions value={value} options={options} />
          </select>
        </div>
        {hasLabel ? (
          <div className={styles['label-container']} data-testid={`${dataTestId}-label`}>
            <Typography className={styles.label} variant="body-regular">
              <span>{label}</span>
              {isRequired ? <span className={styles['required-asterisk']}>*</span> : null}
            </Typography>
            <Typography className={styles['right-value']} variant="body-regular">
              {findOptionName(value)}
            </Typography>
          </div>
        ) : null}
        <span className={styles.icons} data-testid={`${dataTestId}-icons`}>
          <Icon fill={disabled ? 'icon-disabled' : null} name={'chevron_up_chevron_down'}></Icon>
        </span>
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
